import { useState, useEffect, useCallback } from 'react';
import {
  getAllUsers,
  getUserById,
  suspendUser,
  activateUser,
  updateUserCredits,
  updateUserPlan,
  logAdminActivity,
  type GetAllUsersFilters,
  type PaginationParams,
  type UserWithStats,
} from '@/shared/db';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';

export function useUsers(
  initialFilters: GetAllUsersFilters = {},
  initialPagination: PaginationParams = { page: 1, pageSize: 50 }
) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<GetAllUsersFilters>(initialFilters);
  const [pagination, setPagination] = useState<PaginationParams>(initialPagination);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { users: usersData, total: totalCount } = await getAllUsers(
        filters,
        pagination
      );

      setUsers(usersData);
      setTotal(totalCount);
    } catch (err: unknown) {
      console.error('Error fetching users:', err);
      const errorMessage =
        err instanceof Error ? err.message : t('admin.errors.fetchUsersFailed');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const updateFilters = (newFilters: Partial<GetAllUsersFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPagination({ ...pagination, page: 1 }); // Reset to first page
  };

  const updatePagination = (newPagination: Partial<PaginationParams>) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  };

  const handleSuspendUser = async (userId: string) => {
    try {
      await suspendUser(userId);
      if (user?.id) {
        await logAdminActivity(
          user.id,
          'suspend_user',
          'user',
          userId,
          { reason: 'Admin action' }
        );
      }
      toast.success(t('admin.users.actions.suspended'));
      fetchUsers(); // Refresh list
    } catch (err: unknown) {
      console.error('Error suspending user:', err);
      const errorMessage =
        err instanceof Error ? err.message : t('admin.errors.suspendFailed');
      toast.error(errorMessage);
    }
  };

  const handleActivateUser = async (userId: string) => {
    try {
      await activateUser(userId);
      if (user?.id) {
        await logAdminActivity(
          user.id,
          'activate_user',
          'user',
          userId,
          { reason: 'Admin action' }
        );
      }
      toast.success(t('admin.users.actions.activated'));
      fetchUsers(); // Refresh list
    } catch (err: unknown) {
      console.error('Error activating user:', err);
      const errorMessage =
        err instanceof Error ? err.message : t('admin.errors.activateFailed');
      toast.error(errorMessage);
    }
  };

  const handleUpdateCredits = async (userId: string, credits: number) => {
    if (!user?.id) return;
    try {
      await updateUserCredits(userId, credits, user.id);
      if (user?.id) {
        await logAdminActivity(
          user.id,
          'update_credits',
          'user',
          userId,
          { credits }
        );
      }
      toast.success(t('admin.users.actions.creditsUpdated'));
      fetchUsers(); // Refresh list
    } catch (err: unknown) {
      console.error('Error updating credits:', err);
      const errorMessage =
        err instanceof Error ? err.message : t('admin.errors.updateCreditsFailed');
      toast.error(errorMessage);
    }
  };

  const handleUpdatePlan = async (userId: string, plan: 'free' | 'pro') => {
    try {
      await updateUserPlan(userId, plan);
      if (user?.id) {
        await logAdminActivity(
          user.id,
          'update_plan',
          'user',
          userId,
          { plan }
        );
      }
      toast.success(t('admin.users.actions.planUpdated'));
      fetchUsers(); // Refresh list
    } catch (err: unknown) {
      console.error('Error updating plan:', err);
      const errorMessage =
        err instanceof Error ? err.message : t('admin.errors.updatePlanFailed');
      toast.error(errorMessage);
    }
  };

  return {
    users,
    total,
    loading,
    error,
    filters,
    pagination,
    updateFilters,
    updatePagination,
    suspendUser: handleSuspendUser,
    activateUser: handleActivateUser,
    updateCredits: handleUpdateCredits,
    updatePlan: handleUpdatePlan,
    refetch: fetchUsers,
  };
}

export function useUser(userId: string | undefined) {
  const { t } = useTranslation();
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const userData = await getUserById(userId);
      setUser(userData);
    } catch (err: unknown) {
      console.error('Error fetching user:', err);
      const errorMessage =
        err instanceof Error ? err.message : t('admin.errors.fetchUserFailed');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return {
    user,
    loading,
    error,
    refetch: fetchUser,
  };
}

